import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const INWARD_BUSINESS_TZ = "Asia/Kolkata";
export const PERMISSION_TYPE_INVOICE_BACKDATE = "INVOICE_BACKDATE_ACCESS";

export function normalizeYmdIST(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = dayjs(value);
  if (d.isValid()) return d.tz(INWARD_BUSINESS_TZ).format("YYYY-MM-DD");
  const split = raw.split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(split) ? split : "";
}

export function todayYmdIST() {
  return dayjs().tz(INWARD_BUSINESS_TZ).format("YYYY-MM-DD");
}

export function firstDayOfCurrentMonthYmdIST() {
  return dayjs().tz(INWARD_BUSINESS_TZ).startOf("month").format("YYYY-MM-DD");
}

// LEGACY: Permission requests auto-covered 1st of current month (IST) through today.
// export function getPermissionRequestRangeIST() {
//   return {
//     fromYmd: firstDayOfCurrentMonthYmdIST(),
//     toYmd: todayYmdIST(),
//   };
// }

export function getLastThreeDaysRangeIST() {
  const today = dayjs().tz(INWARD_BUSINESS_TZ).startOf("day");
  return {
    minYmd: today.subtract(2, "day").format("YYYY-MM-DD"),
    maxYmd: today.format("YYYY-MM-DD"),
  };
}

export function isWithinLastThreeDaysIST(ymd) {
  if (!ymd) return false;
  const { minYmd, maxYmd } = getLastThreeDaysRangeIST();
  return ymd >= minYmd && ymd <= maxYmd;
}

/** True when entire [fromYmd, toYmd] sits inside the last-3-days free window. */
export function isRangeFullyWithinLastThreeDaysIST(fromYmd, toYmd) {
  if (!fromYmd || !toYmd) return false;
  return isWithinLastThreeDaysIST(fromYmd) && isWithinLastThreeDaysIST(toYmd);
}

export function isYmdWithinInclusiveRange(ymd, fromYmd, toYmd) {
  if (!ymd || !fromYmd || !toYmd) return false;
  return ymd >= fromYmd && ymd <= toYmd;
}

export function getPermissionRangeYmd(permission) {
  if (!permission?.requestedData) return { fromYmd: "", toYmd: "" };
  const fromYmd =
    permission.requestedData.fromDateYmd ||
    normalizeYmdIST(permission.requestedData.fromDate);
  const toYmd =
    permission.requestedData.toDateYmd ||
    normalizeYmdIST(permission.requestedData.toDate);
  return { fromYmd, toYmd };
}

/**
 * Prefer approvedPermissions[] from API; fall back to singular activePermission.
 */
export function resolveApprovedPermissionsList({
  approvedPermissions = null,
  activePermission = null,
} = {}) {
  if (Array.isArray(approvedPermissions) && approvedPermissions.length > 0) {
    return approvedPermissions.filter((p) => {
      const status = String(p?.status || "APPROVED").toUpperCase();
      return status === "APPROVED";
    });
  }
  if (activePermission) {
    const status = String(activePermission.status || "APPROVED").toUpperCase();
    if (status === "APPROVED") return [activePermission];
  }
  return [];
}

export function resolvePendingRequestsList({
  pendingRequests = null,
  pendingRequest = null,
} = {}) {
  if (Array.isArray(pendingRequests) && pendingRequests.length > 0) {
    return pendingRequests.filter(
      (p) => String(p?.status || "PENDING").toUpperCase() === "PENDING",
    );
  }
  if (pendingRequest?.status === "PENDING") return [pendingRequest];
  return [];
}

export function isPermissionExpired(permission) {
  if (!permission) return true;
  // LEGACY: 24-hour access — treated missing expiresAt as expired.
  // if (!permission?.expiresAt) return true;
  // return dayjs().isAfter(dayjs(permission.expiresAt));
  return String(permission.status || "APPROVED").toUpperCase() === "EXPIRED";
}

export function isInvoiceDateInAnyApprovedRange(ymd, approvedPermissions) {
  if (!ymd || !approvedPermissions?.length) return false;
  for (const permission of approvedPermissions) {
    const { fromYmd, toYmd } = getPermissionRangeYmd(permission);
    if (isYmdWithinInclusiveRange(ymd, fromYmd, toYmd)) return true;
  }
  return false;
}

// LEGACY: Approved access used rolling month-start → today (ignored stored range).
// export function isWithinApprovedRangeIST(ymd, activePermission) {
//   if (!ymd || !activePermission?.requestedData) return false;
//   const fromYmd = firstDayOfCurrentMonthYmdIST();
//   const todayYmd = todayYmdIST();
//   if (!fromYmd) return false;
//   return ymd >= fromYmd && ymd <= todayYmd;
// }

export function isDateAllowedForInwardCreate(ymd, permissionState = {}) {
  const normalized = normalizeYmdIST(ymd);
  if (!normalized) return false;
  if (isWithinLastThreeDaysIST(normalized)) return true;

  const approvedPermissions = resolveApprovedPermissionsList(permissionState);
  return isInvoiceDateInAnyApprovedRange(normalized, approvedPermissions);

  // LEGACY: pending request blocked all backdated dates; single activePermission + expiry.
  // if (permissionState.pendingRequest?.status === "PENDING") return false;
  // if (
  //   permissionState.activePermission?.status === "APPROVED" &&
  //   !isPermissionExpired(permissionState.activePermission)
  // ) {
  //   return isWithinApprovedRangeIST(ymd, permissionState.activePermission);
  // }
  // return false;
}

/**
 * Single source of truth for posting date allow-list (create, edit, bulk, picker).
 * Allowed = last 3 IST days UNION any approved permission range.
 * Edit mode additionally constrains to [originalDate, today].
 */
export function isPostingDateAllowed(ymd, permissionState = {}, options = {}) {
  const normalized = normalizeYmdIST(ymd);
  if (!normalized) return false;

  const { isEditMode = false, originalDateYmd = "" } = options;
  const originalYmd = normalizeYmdIST(originalDateYmd);
  if (isEditMode && originalYmd) {
    const today = todayYmdIST();
    if (normalized < originalYmd || normalized > today) return false;
  }

  return isDateAllowedForInwardCreate(normalized, permissionState);
}

export function createPostingDateSelectableChecker(permissionState = {}, options = {}) {
  return (ymd) => isPostingDateAllowed(ymd, permissionState, options);
}

export function requiresBackdatePermission(ymd, permissionState = {}, options = {}) {
  if (!ymd) return false;
  return !isPostingDateAllowed(ymd, permissionState, options);
}

/** True when date is older than today / yesterday / day-before-yesterday (IST). */
export function isOldDateRestricted(ymd) {
  if (!ymd) return false;
  return !isWithinLastThreeDaysIST(ymd);
}

export function hasActivePermissionForDate(ymd, permissionState = {}, options = {}) {
  return isPostingDateAllowed(ymd, permissionState, options);
}

export function resolveValidPostingDateFallback(previousValidYmd) {
  const prev = normalizeYmdIST(previousValidYmd);
  if (prev && isWithinLastThreeDaysIST(prev)) return prev;
  return todayYmdIST();
}

function getEarliestApprovedFromYmd(approvedPermissions) {
  let earliest = null;
  for (const permission of approvedPermissions || []) {
    const { fromYmd } = getPermissionRangeYmd(permission);
    if (fromYmd && (!earliest || fromYmd < earliest)) earliest = fromYmd;
  }
  return earliest;
}

/** Calendar month navigation bounds (not the allow-list — use isPostingDateAllowed per day). */
export function getPostingDateNavigationBounds(
  permissionState = {},
  { isEditMode = false, originalDateYmd = "", activePermission = null, approvedPermissions = null } = {},
) {
  const { minYmd: threeMin, maxYmd } = getLastThreeDaysRangeIST();
  const grants = resolveApprovedPermissionsList({ approvedPermissions, activePermission });
  const originalYmd = normalizeYmdIST(originalDateYmd);

  if (isEditMode && originalYmd) {
    return { min: originalYmd, max: maxYmd };
  }

  if (grants.length > 0) {
    const earliestApproved = getEarliestApprovedFromYmd(grants);
    const min = earliestApproved && earliestApproved < threeMin ? earliestApproved : threeMin;
    return { min, max: maxYmd };
  }

  return { min: threeMin, max: maxYmd };
}

export function getInvoiceDatePickerBoundsIST({
  isEditMode = false,
  originalDateYmd = "",
  activePermission = null,
  approvedPermissions = null,
} = {}) {
  return getPostingDateNavigationBounds(
    { approvedPermissions, activePermission },
    { isEditMode, originalDateYmd, activePermission, approvedPermissions },
  );

  // LEGACY: min/max on native input implied contiguous allowed range (month-start → today).
  // const { minYmd: threeMin, maxYmd } = getLastThreeDaysRangeIST();
  // const grants = resolveApprovedPermissionsList({ approvedPermissions, activePermission });
  // if (isEditMode) {
  //   const min = originalDateYmd || "";
  //   return { min, max: maxYmd };
  // }
  // if (grants.length > 0) {
  //   const earliestApproved = getEarliestApprovedFromYmd(grants);
  //   const min = earliestApproved && earliestApproved < threeMin ? earliestApproved : threeMin;
  //   return { min, max: maxYmd };
  // }
  // if (activePermission?.status === "APPROVED" && !isPermissionExpired(activePermission)) {
  //   return { min: firstDayOfCurrentMonthYmdIST(), max: maxYmd };
  // }
  // return { min: threeMin, max: maxYmd };
}

export function formatPermissionRangeDisplay(fromYmd, toYmd) {
  const fmt = (ymd) => {
    if (!ymd) return "—";
    const d = dayjs.tz(ymd, INWARD_BUSINESS_TZ);
    return d.isValid() ? d.format("DD-MM-YYYY") : ymd;
  };
  const from = fmt(fromYmd);
  const to = fmt(toYmd);
  return from === to ? from : `${from} → ${to}`;
}

export function formatApprovedPermissionsForDisplay(approvedPermissions) {
  return resolveApprovedPermissionsList({ approvedPermissions }).map((permission) => {
    const { fromYmd, toYmd } = getPermissionRangeYmd(permission);
    return {
      id: permission.id || permission._id,
      label: formatPermissionRangeDisplay(fromYmd, toYmd),
      fromYmd,
      toYmd,
    };
  });
}

export function validatePermissionRequestRange(fromYmd, toYmd) {
  const from = normalizeYmdIST(fromYmd);
  const to = normalizeYmdIST(toYmd);
  if (!from) return { ok: false, error: "From date is required." };
  if (!to) return { ok: false, error: "To date is required." };
  if (from > to) return { ok: false, error: "From date cannot be after to date." };
  const today = todayYmdIST();
  if (from > today || to > today) {
    return { ok: false, error: "Future dates are not allowed." };
  }
  if (isRangeFullyWithinLastThreeDaysIST(from, to)) {
    return {
      ok: false,
      error: "Permission is not required for dates within the last 3 days.",
    };
  }
  return { ok: true, fromYmd: from, toYmd: to };
}

export function mapPermissionRequestError(message) {
  const msg = String(message || "").toLowerCase();
  if (msg.includes("pending permission request")) {
    return "You already have a pending request.";
  }
  if (msg.includes("approved permission is still active")) {
    return "You already have active access.";
  }
  if (msg.includes("not required for dates within the last 3")) {
    return "Permission is not required for dates within the last 3 days.";
  }
  return message || "Failed to submit permission request.";
}

export function extractInvoiceDatesFromUploadRows(rows, getCol) {
  const dates = new Set();
  for (const r of rows || []) {
    const raw = getCol(r, [
      "Invoice Date",
      "invoiceDate",
      "invoicedate",
      "date",
    ]);
    if (raw == null || String(raw).trim() === "") continue;
    let ymd = "";
    if (typeof raw === "number" && raw > 30000 && raw < 60000) {
      const excelEpoch = dayjs("1899-12-30");
      ymd = excelEpoch.add(raw, "day").format("YYYY-MM-DD");
    } else {
      ymd = normalizeYmdIST(raw);
    }
    if (ymd) dates.add(ymd);
  }
  return [...dates];
}

export function findDisallowedBulkInvoiceDates(invoiceYmds, permissionState) {
  return (invoiceYmds || []).filter(
    (ymd) => !isPostingDateAllowed(ymd, permissionState),
  );
}
